import { useModelService } from "@shared/services/api/hooks/useModelService";
import { useMemo, useEffect } from "react";
import PublicKeyValueParamsRepository, {
  KeyValueParamSerializer,
} from "../repositories/PublicKeyValueParamsRepository";

export type { KeyValueParamSerializer } from "../repositories/PublicKeyValueParamsRepository";

export const useKeyValueService = () => {
  const repository = useMemo(() => new PublicKeyValueParamsRepository(), []);

  const {
    // Always use pagination
    useList: _,
    usePaginated,
    invalidateSubcategoryQueries,
  } = useModelService(repository);

  return {
    usePaginated,
    invalidateSubcategoryQueries,
  };
};

/**
 * Hook that automatically fetches all pages of key-value parameters
 * and provides a flattened array and lookup object
 */
export const useKeyValueParams = () => {
  const { usePaginated } = useKeyValueService();
  const { data, fetchNextPage, hasNextPage, isLoading, error, isFetching } =
    usePaginated({
      params: {
        page_size: 20,
      },
    });

  // Auto-fetch all pages when data becomes available
  useEffect(() => {
    if (!isLoading && !isFetching && hasNextPage) {
      fetchNextPage();
    }
  }, [isLoading, isFetching, hasNextPage, fetchNextPage]);

  // Flatten all results from all pages
  const allResults = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) => page.results || []);
  }, [data?.pages]);

  // Create lookup object for O(1) access by key
  const lookup = useMemo(() => {
    return allResults.reduce((acc, param) => {
      acc[param.key] = param;
      return acc;
    }, {} as Record<string, KeyValueParamSerializer>);
  }, [allResults]);

  return {
    data: allResults,
    lookup,
    isLoading: isLoading || (hasNextPage && isFetching),
    error,
  };
};
